/*
 * Storefront dev mode, server side (documents/helpers/devMode.md). Added for jument; shared by every site of this core.
 * Without the `_dev_mode` cookie every function here answers "not in dev mode" and nothing else changes.
 *
 * The cookie's token is checked with the backend (checkDevModeToken) — once a minute per token in this process for
 * page rendering, fresh for /apis/dev-mode — and the API url in `_dev_api_url` is only called by this server when it is
 * on the backend's list (or is localhost on an app that runs locally): that cookie is the browser's to write.
 */
import { DEV_MODE_COOKIES, api_url_allowed } from "@core/helpers/dev-mode-rules";

const CHECK_TTL_MS = 60 * 1000;
const checks = new Map();

// a local API only for an app that runs locally — decided by how it was started, never by the request's Host header
export const app_is_local_server = () => process.env.NODE_ENV === "development" || process.env.DEV_MODE_ALLOW_LOCAL_API === "1";

const default_api_url = () => process.env.LOCAL_GRAPHQL_URL || process.env.GRAPHQL_URL || process.env.NEXT_PUBLIC_GRAPHQL_URL;

const CHECK_QUERY = `query ($token: String!) {
  result: checkDevModeToken(token: $token) { ok reason expires_at sites { id name nickname } api_endpoints { key title url needs_port } }
}`;

/*
 * What the backend says a token opens for `site_url` (the live site's storefront address).
 * -> { ok, reason, expires_at, sites, api_endpoints }; a backend that does not answer -> { ok: false, reason: "check_failed" }
 */
export const check_dev_token_server = async (token, site_url, { fresh = false, now = Date.now() } = {}) => {
	const key = `${site_url}|${token}`;
	const cached = checks.get(key);
	if (!fresh && cached && now - cached.at < CHECK_TTL_MS) {
		return cached.result;
	}
	let result;
	try {
		const response = await fetch(default_api_url(), {
			method: "POST",
			headers: { "content-type": "application/json", "site-url": site_url },
			body: JSON.stringify({ query: CHECK_QUERY, variables: { token } }),
			cache: "no-store",
		});
		const body = await response.json();
		result = body?.data?.result || { ok: false, reason: "check_failed" };
	} catch {
		result = { ok: false, reason: "check_failed" };
	}
	// a failed check is not kept: the next request asks again
	if (result.reason !== "check_failed") {
		checks.set(key, { result, at: now });
		if (checks.size > 500) {
			checks.delete(checks.keys().next().value);
		}
	}
	return result;
};

// the dev mode cookies of this request, as they are; {} outside a request
export const read_dev_cookies_server = async () => {
	try {
		const { cookies } = require("next/headers");
		const jar = await cookies();
		return Object.fromEntries(Object.values(DEV_MODE_COOKIES).map((name) => [name, jar.get(name)?.value || ""]));
	} catch {
		return {};
	}
};

/*
 * This request's dev mode for GraphQL calls: null, or { token, site_id, api_url } where api_url is null unless the
 * backend's list allows it (the default API is used then, and the backend still refuses a bad token itself).
 */
export const get_dev_mode_server = async () => {
	const values = await read_dev_cookies_server();
	const token = values[DEV_MODE_COOKIES.token];
	if (!token) {
		return null;
	}
	let api_url = values[DEV_MODE_COOKIES.api_url] || null;
	if (api_url) {
		const { detect_site_url } = require("@core/helpers/api-headers");
		const check = await check_dev_token_server(token, await detect_site_url());
		if (!api_url_allowed({ api_url, endpoints: check?.api_endpoints || [], app_is_local: app_is_local_server() })) {
			api_url = null;
		}
	}
	return { token, site_id: values[DEV_MODE_COOKIES.site] || null, api_url };
};

// whether this request is in dev mode (the cookie only, no check): for a layout to mount the dev mode button
export const in_dev_mode_server = async () => Boolean((await read_dev_cookies_server())[DEV_MODE_COOKIES.token]);
